import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth header to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = async (username, password, phoneNumber, registeredAs = 'coach', email = null, discountCode = null, signupSource = null, refCode = null) => {
  const payload = {
    username,
    password,
    phone_number: phoneNumber || '',
    registered_as: registeredAs,
  };
  if (email) payload.email = email;
  if (discountCode) payload.discount_code = discountCode;
  if (signupSource) payload.signup_source = signupSource;
  if (refCode) payload.ref_code = refCode;
  const response = await api.post('/auth/register', payload);
  return response.data;
};

export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const checkTeamAccess = async (teamId) => {
  const response = await api.get(`/auth/check-access/${teamId}`);
  return response.data;
};

// Teams
export const fetchTeams = async () => {
  const response = await api.get('/teams');
  return response.data;
};

export const fetchPublicTeams = async (search = '') => {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await api.get(`/teams/public${params}`);
  return response.data;
};

export const createTeam = async (teamData) => {
  const response = await api.post('/teams', teamData);
  return response.data;
};

export const getTeam = async (teamId) => {
  const response = await api.get(`/teams/${teamId}`);
  return response.data;
};

export const updateTeam = async (teamId, teamData) => {
  const response = await api.put(`/teams/${teamId}`, teamData);
  return response.data;
};

export const deleteTeam = async (teamId) => {
  const response = await api.delete(`/teams/${teamId}`);
  return response.data;
};

// Coach Management
export const addCoachPhone = async (teamId, phoneNumber) => {
  const response = await api.post(`/teams/${teamId}/coaches`, { phone_number: phoneNumber });
  return response.data;
};

export const removeCoachPhone = async (teamId, phone) => {
  const response = await api.delete(`/teams/${teamId}/coaches/${encodeURIComponent(phone)}`);
  return response.data;
};

export const getCoachPhones = async (teamId) => {
  const response = await api.get(`/teams/${teamId}/coaches`);
  return response.data;
};

export const searchUsers = async (phone) => {
  const response = await api.get(`/users/search?phone=${encodeURIComponent(phone)}`);
  return response.data;
};

// Players
export const addPlayer = async (teamId, playerData) => {
  const response = await api.post(`/teams/${teamId}/players`, playerData);
  return response.data;
};

export const updatePlayer = async (teamId, playerId, playerData) => {
  const response = await api.put(`/teams/${teamId}/players/${playerId}`, playerData);
  return response.data;
};

export const deletePlayer = async (teamId, playerId) => {
  const response = await api.delete(`/teams/${teamId}/players/${playerId}`);
  return response.data;
};

// Games
export const fetchGames = async (teamId, season) => {
  const params = new URLSearchParams();
  if (teamId) params.append('team_id', teamId);
  if (season) params.append('season', season);
  const response = await api.get(`/games?${params.toString()}`);
  return response.data;
};

export const fetchPublicGames = async (teamId) => {
  const params = teamId ? `?team_id=${teamId}` : '';
  const response = await api.get(`/games/public${params}`);
  return response.data;
};

export const getPublicGame = async (gameId) => {
  const response = await api.get(`/games/public/${gameId}`);
  return response.data;
};

export const createGame = async (gameData) => {
  const response = await api.post('/games', gameData);
  return response.data;
};

export const getGame = async (gameId) => {
  const response = await api.get(`/games/${gameId}`);
  return response.data;
};

export const updateGame = async (gameId, gameData) => {
  const response = await api.put(`/games/${gameId}`, gameData);
  return response.data;
};

export const deleteGame = async (gameId) => {
  const response = await api.delete(`/games/${gameId}`);
  return response.data;
};

// Stats
export const getPlayerStats = async (playerId, season) => {
  const params = season ? `?season=${season}` : '';
  const response = await api.get(`/stats/player/${playerId}${params}`);
  return response.data;
};

export const getTeamStats = async (teamId, season) => {
  const params = season ? `?season=${season}` : '';
  const response = await api.get(`/stats/team/${teamId}${params}`);
  return response.data;
};

export const getSprayChartData = async (playerId, gameId, season) => {
  const params = new URLSearchParams();
  if (gameId) params.append('game_id', gameId);
  if (season) params.append('season', season);
  const response = await api.get(`/stats/spray/${playerId}?${params.toString()}`);
  return response.data;
};

// Seasons
export const scanLineupCard = async (imageBase64) => {
  const response = await api.post('/scan-lineup', { image_base64: imageBase64 });
  return response.data;
};

export const generateGameSummary = async (gameId) => {
  const response = await api.post(`/games/${gameId}/generate-summary`);
  return response.data;
};

export const fetchSeasons = async (teamId) => {
  const params = teamId ? `?team_id=${teamId}` : '';
  const response = await api.get(`/seasons${params}`);
  return response.data;
};

export const createSeason = async (seasonData) => {
  const response = await api.post('/seasons', seasonData);
  return response.data;
};

// Subscriptions
export const getSubscriptionPlans = async () => {
  const response = await api.get('/subscription/plans');
  return response.data;
};

export const getSubscriptionStatus = async () => {
  const response = await api.get('/subscription/status');
  return response.data;
};

export const createSubscriptionCheckout = async (planId) => {
  const response = await api.post('/subscription/checkout', {
    plan_id: planId,
    origin_url: window.location.origin
  });
  return response.data;
};

export const checkCheckoutStatus = async (sessionId) => {
  const response = await api.get(`/subscription/checkout/status/${sessionId}`);
  return response.data;
};

export default api;
